// 성능 저하 신고소 — 「그림은 같고 «속도만» 잃는 폴백」을 한 자리에 모은다.
//
// 이 리포의 폴백은 대부분 **원리적으로 눈에 안 보인다**: 구운 밭과 만든 밭은 같은 그림이고,
// 워커가 죽어 메인스레드로 내려앉아도 화면은 같다. 잃는 건 프레임뿐이라 「가끔 렉 걸리네」로만
// 읽히고, 그래서 몇 주씩 방치된다. 각 폴백이 제자리에서 `console.warn` 한 줄을 찍는 방식은
// 이미 실패했다 — 흩어져 있으면 아무도 세지 않는다.
//
// 규약: 저하 경로로 «내려앉는 그 자리»에서 `noteDegrade()` 를 부른다. 같은 id 는 한 번만 센다.
//   · 굽기가 «낡아서» 생긴 저하는 여기 신고하지 말고 **터뜨려라**(`LOD_STRICT_BAKED`).
//     사람이 잊어서 나는 것은 알림으로 안 고쳐진다 — 그건 배포 게이트와 예외가 맡는다.
//   · 여기 오는 것은 «기기가 못 해서» 나는 저하다(워커 없음·OffscreenCanvas 없음 등).
//     그건 터뜨리면 그 기기에서 게임이 안 도니까, 대신 **숨을 수 없게** 한다.
//
// 읽는 법: 게임 안 토스트 · `window.__degrade()` (헤드리스·QA) · 서버 client_debug 로그.
import { reportEventNow } from './clientLogReporter';
import { toast } from '@ui/overlay/toastStore';

/** id → { what, cost, fix, at } */
const _seen = new Map();

/** 지금까지 신고된 저하 전부. 신고 순서대로. */
export function list() { return [..._seen.values()]; }

/**
 * 저하 경로로 내려앉았음을 신고한다. **같은 id 는 한 번만** 센다(매 프레임 불러도 안전).
 *
 * @param {string} id    저하 한 종류를 가리키는 안정된 키. 예: 'worker:chunkMesh'
 * @param {object} o
 * @param {string} o.what  무엇이 느려졌나 — 사람이 읽을 한 줄
 * @param {string} [o.cost] 대가. **실측이 있을 때만** 적는다(예: '창 하나 51ms').
 *                          추정치를 적지 마라 — 근거 없는 숫자는 다음 사람을 오도한다.
 * @param {string} [o.fix]  고치는 법 한 줄(명령·플래그). 없으면 생략.
 * @returns {boolean} 이번 호출이 «처음» 신고인가
 */
export function noteDegrade(id, { what, cost = '', fix = '' } = {}) {
  if (!id || _seen.has(id)) return false;
  _seen.set(id, { id, what, cost, fix, at: Date.now() });

  const tail = [cost && `대가 ${cost}`, fix && `고치기: ${fix}`].filter(Boolean).join(' · ');
  // console.warn 이 아니라 error 다 — 이 리포의 콘솔은 warn 이 흔해서 warn 은 안 읽힌다.
  console.error(`[저하] ${what}${tail ? ` — ${tail}` : ''}  (id=${id})`);

  // 서버로 한 번 올린다. 실기·폰에서 나는 저하는 콘솔을 볼 사람이 없다.
  try { reportEventNow('perf-degrade', `${id} | ${what}${tail ? ` | ${tail}` : ''}`); } catch { /* 로그가 막혀도 게임은 산다 */ }

  // 화면에도 낸다. 오래 띄운다 — 이 토스트는 «알림»이 아니라 «청구서»다.
  try {
    toast.err(`성능 저하 — ${what}`, { detail: tail || undefined, ms: 15000 });
  } catch { /* 표시부가 아직 없어도 신고는 남는다 */ }

  return true;
}

// 헤드리스·QA 창구 — `window.__degrade()` 로 이 세션이 어느 폴백을 탔는지 한 번에 본다.
if (typeof window !== 'undefined') window.__degrade = list;
